/**
 * Share a book with people (part 38b; cockatrice-ui `ShareDialog.jsx`; decisions 75, 63; tables IW and IR): the invite
 * row (an address, a role, Invite), the role's help; the people: you, the owner, then each person invited, with what
 * they can do and where the invite stands (not accepted yet, accepted, needs a new invite), their settings opened in
 * place (role, manuscript Read, notes None/Read/Write, export, an end date, the chapters), Save changes and Remove
 * access, asked first; Resend and Send a new invite; and the invite link's step (Copy link, Email it, Share…), which the
 * writer sends themselves (principle 10). Only a paid account invites (DI10): any other is told inviting comes with the
 * purchase, the people it has still managed. The relay's work is `Sharing`'s; the dialog says each outcome once.
 * PLAN.md §3, step 6: built from the component library (TextField, Select, Button, Avatar, SegmentedControl, Checkbox,
 * InlineConfirm, CopyField). Not AccessControl: its manuscript choice offers Suggest and Edit, its presets Co-author and
 * its role is worked out from the two choices, where ours is Read only, three presets chosen by name (decisions 5, 75).
 */
import { createMemo, createSignal, onMount } from 'solid-js';
import type { Accessor } from 'solid-js';
import type { ChapterEntry, InviteNotes, InviteRange, InviteRecord, InviteRole, ShareRecord } from '@cockatrice/storage';
import { INVITE_NOTES, INVITE_ROLES } from '@cockatrice/storage';
import { orderChapters } from '@cockatrice/core';
import { canonicalEmail } from '../account/email.ts';
import { DAY_MS } from '../account/trial.ts';
import { chaptersOf, MAX_INVITE_LIFE_MS } from '../share/copies.ts';
import type { RelayError } from '../account/client.ts';
import type { OwnerInvite } from '../share/invite-wire.ts';
import type { ChangeOutcome, InviteChoice, InviteOutcome } from '../share/invites.ts';
import { dateWords } from './account-sheet.ts';
import { focusOn } from './announce.ts';
import { shownBook, shownTitle } from './chapter-list.ts';
import { el, each, when, whenSome } from '../ui/dom.ts';
import type { Child } from '../ui/dom.ts';
import Avatar from '../ui/Avatar.ts';
import type { AvatarTone } from '../ui/Avatar.ts';
import Button from '../ui/Button.ts';
import Checkbox from '../ui/Checkbox.ts';
import CopyField from '../ui/CopyField.ts';
import Icon from '../ui/Icon.ts';
import type { IconName } from '../ui/Icon.ts';
import InlineConfirm from '../ui/InlineConfirm.ts';
import SegmentedControl from '../ui/SegmentedControl.ts';
import Select from '../ui/Select.ts';
import TextField, { TextInput } from '../ui/TextField.ts';
import { component } from '../ui/solid.ts';
import { ModalActions, ModalBody, ModalButtons, ModalFoot, ModalHead, ModalNote } from './modal.ts';
import type { Shell } from './shell.ts';
import { dayOf, endOfDay } from './share-dialog.ts';

/** Decision 75: the notes level each role presets (the design's Editor and Proofreader write, a Beta reader reads). */
export const ROLE_NOTES: Readonly<Record<InviteRole, InviteNotes>> = { editor: 'write', 'beta-reader': 'read', proofreader: 'write' };

/** The invite's place as the dialog and the Sharing page say it (table IR, part 38a's IS). */
export type InviteStanding = 'unsent' | 'pending' | 'accepted' | 'lost' | 'ended';

/** Table IR: where `invite` stands, from the record and the relay's last word `listed` on it, at `now`. */
export function standingOf(invite: InviteRecord, listed: Pick<OwnerInvite, 'state' | 'endsAt'> | undefined, now: number): InviteStanding {
  // Deep-fix r8, CS′: a listed invite's end is the listed one (moved on the other device, moved here too).
  const state = listed?.state;
  const endsAt = listed === undefined ? invite.endsAt : listed.endsAt;
  if (invite.removedAt !== null || state === 'removed' || state === 'share-ended' || state === 'expired' || (endsAt !== null && endsAt <= now)) return 'ended';
  if (state === 'lost') return 'lost';
  if (invite.sent === null) return 'unsent';
  return state === 'claimed' ? 'accepted' : 'pending';
}

/**
 * Deep-fix r9, table CS″: `invite` as it is shown and seeded: for one the relay lists (`listed`), its listed role,
 * notes, export and end (another device's change shown as it stands); for one it doesn't (unsent), its own. The chapter
 * range stays the record's (the relay holds explicit chapters, not the range).
 */
export function shownOf(invite: InviteRecord, listed: Pick<OwnerInvite, 'role' | 'notes' | 'export' | 'endsAt'> | undefined): InviteRecord {
  return listed === undefined ? invite : { ...invite, role: listed.role, notes: listed.notes, export: listed.export, endsAt: listed.endsAt };
}

/** A person's initials from their address: the first two letters or digits of its local part, in capitals. */
export function initialsOf(email: string): string {
  const letters = email.slice(0, Math.max(0, email.indexOf('@'))).match(/[\p{L}\p{N}]/gu) ?? [];
  return letters.slice(0, 2).join('').toLocaleUpperCase() || '@';
}

/** The relay's refusals of an invite in the dialog's words; any other is a failure. */
const INVITE_REFUSALS: Partial<Readonly<Record<RelayError, 'invite.error.purchase' | 'invite.error.full' | 'invite.error.today' | 'invite.error.ended' | 'invite.error.settings'>>> = {
  // Deep-fix r10, CS‴: settings the relay won't hold are said as such, never as the network.
  'bad-request': 'invite.error.settings',
  'purchase-required': 'invite.error.purchase',
  'share-full': 'invite.error.full',
  'rate-limited': 'invite.error.today',
  'share-ended': 'invite.error.ended',
  'share-gone': 'invite.error.ended',
  'invite-ended': 'invite.error.ended',
};

/** A refusal or failure in the dialog's words. */
function problemOf(shell: Shell, outcome: Exclude<InviteOutcome | ChangeOutcome, { readonly kind: 'invited' | 'unsent' | 'done' }>): string {
  const key = outcome.kind === 'refused' ? INVITE_REFUSALS[outcome.error] : undefined;
  return shell.t(key ?? 'invite.error.failed');
}

/** Deep-fix r3, BU′: the open book's open share records (not ended): more than one when an update staged another. */
function openShares(shell: Shell): readonly ShareRecord[] {
  const read = shell.sharing.shares();
  const book = shell.workspace.book();
  if (read.kind !== 'ok' || book === null) return [];
  return read.value.filter((record) => record.bookId === book.id && record.ended === null);
}

/** BU′: the record of `records` holding invite `id`, if any. */
function holderOf(records: readonly ShareRecord[], id: string): ShareRecord | undefined {
  return records.find((record) => record.invites.some((invite) => invite.id === id));
}

/** A small icon beside words that say the same. */
function mark(name: IconName): Child {
  return component(Icon, { name, size: 'sm' });
}

/** RC16: what stopped book `bookId`'s copy to the people it is shared with, in words; nothing when nothing did. */
export function CopyProblem(shell: Shell, bookId: Accessor<string | undefined>): Child {
  return whenSome(
    () => {
      const id = bookId();
      return id === undefined ? undefined : shell.sharing.problemOf(id);
    },
    (problem) => el('p', { class: 'sh-help sh-warn copy-problem' }, mark('warn'), () => shell.t(`invite.problem.${problem()}`)),
  );
}

/** The open book's title as shown. */
function bookTitle(shell: Shell): string {
  const book = shell.workspace.book();
  return book === null ? shell.t('book.unreadable') : shownBook(shell, book);
}

/** The invite step's link: whom it is for and the URL; `unsent` when the relay hasn't taken it yet. */
interface LinkStep {
  readonly email: string;
  readonly url: string;
  readonly unsent: boolean;
}

/** The dialog's head: its title (the dialog's name) and Close. */
function Head(shell: Shell, title: Accessor<string>): Child {
  // Part 11g: the modal's head (`modal.ts`).
  return ModalHead(shell, title, { closeClass: 'share-close' });
}

/** The chapters' choice: All chapters, or a From/To range of the book's chapters in order (table CS). */
function Chapters(shell: Shell, id: string, chapters: readonly ChapterEntry[], chosen: Accessor<InviteRange | null>, given: Accessor<readonly string[]>, set: (value: InviteRange | null) => void): Child {
  const { t } = shell;
  // Table CS: the range's ends as the first and last live chapters it gives (none given: the book's first and last);
  // read only while a range is chosen (the selects show then).
  const places = (): readonly number[] => given().map((chapter) => chapters.findIndex((entry) => entry.id === chapter)).filter((at) => at >= 0);
  const first = (): number => (places().length === 0 ? 0 : Math.min(...places()));
  const last = (): number => (places().length === 0 ? chapters.length - 1 : Math.max(...places()));
  const range = (from: number, to: number): void => {
    const [start, end] = [chapters[Math.min(from, to)], chapters[Math.max(from, to)]];
    if (start !== undefined && end !== undefined) set({ from: start.id, to: end.id });
  };
  const options = chapters.map((chapter, index) => ({ value: String(index), label: t('share.chapterOption', { number: index + 1, title: shownTitle(shell, chapter) }) }));
  const select = (which: 'from' | 'to', label: string, value: Accessor<number>): Child =>
    component(Select, {
      id: `${id}-${which}`,
      label,
      options,
      get value() {
        return String(value());
      },
      onChange: (picked: string) => {
        const at = Number(picked);
        if (which === 'from') range(at, last());
        else range(first(), at);
      },
    });
  return el(
    'div',
    { class: 'sh-field invite-chapters', id: `${id}-choice` },
    el('span', { id: `${id}-label`, class: 'sh-access__label' }, t('share.chapters')),
    el(
      'div',
      {},
      component(SegmentedControl, {
        'aria-labelledby': `${id}-label`,
        options: [
          { value: 'all', label: t('share.all') },
          { value: 'some', label: t('share.some') },
        ],
        get value() {
          return chosen() === null ? 'all' : 'some';
        },
        onChange: (value: string) => {
          if (value === 'all') set(null);
          // From All: the whole book (no chosen chapter: the first and the last).
          else if (chosen() === null) range(first(), last());
        },
      }),
      when(
        () => chosen() !== null,
        () => el('div', { class: 'sh-range' }, select('from', t('share.from'), first), select('to', t('share.to'), last)),
      ),
    ),
  );
}

/** What a person's chapters are, as the design's link says them: all, one, or a run from first to last, opening the choice. */
export function chaptersWords(shell: Pick<Shell, 't'>, chapters: readonly ChapterEntry[], chosen: readonly string[] | null): string {
  // `chosen`: the chapters a range gives (table CS), or null for All.
  const { t } = shell;
  if (chosen === null) return t('invite.chaptersAll');
  const places = chosen.map((id) => chapters.findIndex((entry) => entry.id === id) + 1).filter((place) => place > 0);
  if (places.length === 0) return t('invite.chaptersNone');
  const first = Math.min(...places);
  const last = Math.max(...places);
  return first === last ? t('invite.chaptersOne', { number: first }) : t('invite.chaptersSome', { from: first, to: last });
}

/** A person's settings, opened in place: role, manuscript, notes, export, the end, the chapters; Save changes and Remove access. */
function Settings(shell: Shell, invite: InviteRecord, say: (text: string) => void, close: () => void): Child {
  const { t, sharing, workspace } = shell;
  const id = `person-${invite.id}`;
  const listing = workspace.listing();
  const chapters = listing?.chapters ?? [];
  // Table CS: the book's order, the trashed too, so a range's ends in the trash still mark its span.
  const order = orderChapters([...chapters, ...(listing?.trash ?? [])]).map((entry) => entry.id);
  const [role, setRole] = createSignal<InviteRole>(invite.role);
  const [notes, setNotes] = createSignal<InviteNotes>(invite.notes);
  const [exported, setExported] = createSignal(invite.export);
  const [ends, setEnds] = createSignal(invite.endsAt === null ? '' : dayOf(invite.endsAt - 1));
  const [chosen, setChosen] = createSignal<InviteRange | null>(invite.chapters);
  const given = (): readonly string[] => (chosen() === null ? chapters.map((entry) => entry.id) : chaptersOf({ chapters: chosen() }, { chapters, order }));
  const [busy, setBusy] = createSignal(false);
  const [choosing, setChoosing] = createSignal(false);
  const today = dayOf(shell.now());
  // CS‴: the relay's longest access (part 38a's `MAX_INVITE_LIFE_MS`), the last day that ends within it.
  const latest = dayOf(shell.now() + MAX_INVITE_LIFE_MS - DAY_MS);
  // Save changes is the only way here, and a loading Button ignores presses: no second save while one runs.
  const save = async (): Promise<void> => {
    const endsAt = ends() === '' ? null : endOfDay(ends());
    if (ends() !== '' && (endsAt === null || ends() < today)) {
      say(t('share.error.date'));
      focusOn(shell, `#${id}-ends`);
      return;
    }
    if (endsAt !== null && endsAt > shell.now() + MAX_INVITE_LIFE_MS) {
      say(t('invite.error.far'));
      focusOn(shell, `#${id}-ends`);
      return;
    }
    const book = workspace.book();
    if (book === null) return;
    const choice: InviteChoice = { role: role(), notes: notes(), export: exported(), endsAt, chapters: chosen() };
    setBusy(true);
    const changed = await sharing.change(book.id, invite.id, choice).finally(() => setBusy(false));
    say(changed.kind === 'done' ? t('invite.saved', { email: invite.email }) : problemOf(shell, changed));
    if (changed.kind === 'done') close();
  };
  const remove = async (): Promise<void> => {
    const book = workspace.book();
    if (book === null || busy()) return;
    setBusy(true);
    const removed = await sharing.remove(book.id, invite.id).finally(() => setBusy(false));
    say(removed.kind === 'done' ? t('invite.removed', { email: invite.email }) : problemOf(shell, removed));
    focusOn(shell, removed.kind === 'done' ? '#invite-email' : `#${id} .remove-access`);
  };
  return el(
    'div',
    { class: 'sh-person__settings', id: `${id}-settings` },
    el(
      'div',
      { class: 'sh-access' },
      el('label', { class: 'sh-access__label', for: `${id}-role` }, t('invite.role')),
      el(
        'div',
        {},
        component(Select, {
          id: `${id}-role`,
          options: INVITE_ROLES.map((each) => ({ value: each, label: t(`invite.roleName.${each}`) })),
          get value() {
            return role();
          },
          'aria-describedby': `${id}-role-help`,
          onChange: (picked: string) => {
            const next = INVITE_ROLES.find((known) => known === picked) ?? invite.role;
            setRole(next);
            setNotes(ROLE_NOTES[next]);
          },
        }),
        el('p', { class: 'sh-help', id: `${id}-role-help` }, () => t('invite.roleGroup')),
      ),
      el('span', { class: 'sh-access__label' }, t('invite.manuscript')),
      el('div', {}, el('strong', { class: 'invite-manuscript' }, t('invite.read')), el('p', { class: 'sh-help' }, t('invite.readHelp'))),
      el('span', { class: 'sh-access__label', id: `${id}-notes` }, t('invite.notes')),
      el(
        'div',
        {},
        component(SegmentedControl, {
          'aria-labelledby': `${id}-notes`,
          options: INVITE_NOTES.map((level) => ({ value: level, label: t(`invite.notes.${level}`) })),
          get value() {
            return notes();
          },
          onChange: (picked: string) => {
            for (const level of INVITE_NOTES.filter((known) => known === picked)) setNotes(level);
          },
        }),
        el('p', { class: 'sh-help' }, () => t(`invite.notesHelp.${notes()}`)),
      ),
    ),
    el(
      'div',
      { class: 'sh-checks' },
      // The help under the box is its own line, so the box's name stays its label alone.
      component(Checkbox, {
        id: `${id}-export`,
        label: t('invite.export'),
        get checked() {
          return exported();
        },
        'aria-describedby': `${id}-export-help`,
        onChange: (on: boolean) => {
          setExported(on);
        },
      }),
      el('p', { class: 'sh-help', id: `${id}-export-help` }, t('invite.exportHelp')),
      el(
        'div',
        { class: 'sh-wrap' },
        component(Checkbox, {
          id: `${id}-ends-on`,
          label: t('invite.ends'),
          get checked() {
            return ends() !== '';
          },
          onChange: (on: boolean) => {
            setEnds(on ? dayOf(shell.now() + 30 * 24 * 60 * 60 * 1000) : '');
          },
        }),
        when(
          () => ends() !== '',
          () =>
            // A date: TextField's own input, which takes any type.
            component(TextInput, {
              id: `${id}-ends`,
              type: 'date',
              class: 'invite-ends',
              min: today,
              max: latest,
              'aria-label': t('invite.endsDate'),
              get value() {
                return ends();
              },
              onChange: (value: string) => {
                setEnds(value);
              },
            }),
        ),
      ),
    ),
    when(choosing, () => Chapters(shell, `${id}-chapters`, chapters, chosen, given, setChosen)),
    el(
      'div',
      { class: 'sh-person__actions' },
      // The design's "Only chapters 1–7…": what the person has, opening the choice of chapters.
      component(Button, {
        variant: 'link',
        class: 'choose-chapters',
        get 'aria-expanded'() {
          return String(choosing());
        },
        'aria-controls': `${id}-chapters-choice`,
        onClick: () => setChoosing(!choosing()),
        get children() {
          return chaptersWords(shell, chapters, chosen() === null ? null : given());
        },
      }),
      el(
        'div',
        { class: 'sh-person__end' },
        // Asked first, Keep it the first answer; the question says what removing ends.
        component(InlineConfirm, {
          label: t('invite.remove'),
          message: t('invite.removeAsk', { email: invite.email }),
          cancelLabel: t('sharing.keep'),
          confirmLabel: t('invite.removeConfirm'),
          triggerClass: 'remove-access ui-inline-confirm__trigger ui-inline-confirm__trigger--danger',
          onConfirm: () => void remove(),
        }),
        component(Button, {
          variant: 'primary',
          size: 'sm',
          class: 'save-person',
          get loading() {
            return busy();
          },
          onClick: () => void save(),
          get children() {
            return busy() ? t('invite.saving') : t('invite.save');
          },
        }),
      ),
    ),
  );
}

/** The words under a person's address: their role and where the invite stands (`accepted`: when, as the relay says). */
export function standingWords(shell: Pick<Shell, 't' | 'locale'>, invite: Pick<InviteRecord, 'role'>, standing: InviteStanding, accepted: number | null): string {
  const { t } = shell;
  const role = t(`invite.roleName.${invite.role}`);
  switch (standing) {
    case 'unsent':
      return t('invite.standing.unsent', { role });
    case 'pending':
      return t('invite.standing.pending', { role });
    case 'accepted':
      return accepted === null ? t('invite.standing.acceptedSome', { role }) : t('invite.standing.accepted', { role, date: dateWords({ locale: shell.locale }, accepted) });
    case 'lost':
      return t('invite.standing.lost');
    case 'ended':
      return t('invite.standing.ended');
  }
}

/** One person: their initials, address and standing, their role (opening their settings), Resend or Send a new invite. */
function Person(shell: Shell, record: Accessor<ShareRecord>, invite: Accessor<InviteRecord>, open: Accessor<string | null>, setOpen: (id: string | null) => void, link: (step: LinkStep) => void, say: (text: string) => void): Child {
  const { t, sharing, workspace } = shell;
  // The invite's ID, its address and settings as they are now (the record read again keeps the row).
  const id = `person-${invite().id}`;
  const listed = (): OwnerInvite | undefined => sharing.listedOf(record(), invite());
  // CS″: the role and the rest as the relay holds them, for this row and the settings it opens.
  const shown = (): InviteRecord => shownOf(invite(), listed());
  const standing = (): InviteStanding => standingOf(invite(), listed(), shell.now());
  const accepted = (): number | null => listed()?.claimedAt ?? null;
  const [busy, setBusy] = createSignal(false);
  // Send a new invite is the only way here, and a loading Button ignores presses: no second renewal while one runs.
  const renew = async (): Promise<void> => {
    const book = workspace.book();
    if (book === null) return;
    setBusy(true);
    const renewed = await sharing.renew(book.id, invite().id).finally(() => setBusy(false));
    if (renewed.kind === 'invited' || renewed.kind === 'unsent') link({ email: invite().email, url: renewed.url, unsent: renewed.kind === 'unsent' });
    else say(problemOf(shell, renewed));
  };
  const resend = (): void => {
    link({ email: invite().email, url: sharing.linkOf(invite()), unsent: invite().sent === null });
  };
  const waiting = (): boolean => standing() === 'pending' || standing() === 'unsent';
  const tone = (): AvatarTone => (waiting() ? 'neutral' : shown().role === 'beta-reader' ? 'reader' : 'editor');
  return el(
    'li',
    { id, class: () => (open() === invite().id ? 'sh-person sh-person--open' : 'sh-person'), 'data-invite': invite().id },
    el(
      'div',
      { class: 'sh-person__row' },
      component(Avatar, {
        get tone() {
          return tone();
        },
        get children() {
          return waiting() ? '@' : initialsOf(invite().email);
        },
      }),
      el('div', { class: 'sh-grow' }, el('strong', { class: 'sh-break' }, invite().email), el('div', { class: 'sh-help person-standing' }, () => standingWords(shell, invite(), standing(), accepted()))),
      when(
        () => standing() === 'lost',
        () =>
          component(Button, {
            size: 'sm',
            class: 'renew-invite',
            get loading() {
              return busy();
            },
            'aria-label': t('invite.renewLabel', { email: invite().email }),
            onClick: () => void renew(),
            children: t('invite.renew'),
          }),
        () => [
          when(waiting, () => component(Button, { variant: 'link', class: 'resend-invite', 'aria-label': t('invite.resendLabel', { email: invite().email }), onClick: resend, children: t('invite.resend') })),
          component(Button, {
            variant: 'ghost',
            size: 'sm',
            class: 'change-person',
            get 'aria-expanded'() {
              return String(open() === invite().id);
            },
            'aria-controls': `${id}-settings`,
            get 'aria-label'() {
              return t('invite.changeLabel', { email: invite().email, role: t(`invite.roleName.${shown().role}`) });
            },
            onClick: () => {
              setOpen(open() === invite().id ? null : invite().id);
            },
            get children() {
              return t(`invite.roleName.${shown().role}`);
            },
          }),
        ],
      ),
    ),
    when(
      () => open() === invite().id,
      () =>
        Settings(shell, shown(), say, () => {
          setOpen(null);
          focusOn(shell, `#${id} .change-person`);
        }),
    ),
  );
}

/** The invite link's step: whom it is for, the link with Copy link, Email it and Share…, and what to know. */
function Link(shell: Shell, step: LinkStep, back: () => void): Child {
  const { t } = shell;
  const [news, setNews] = createSignal('');
  const title = bookTitle(shell);
  const subject = t('invite.mailSubject', { title });
  const body = t('invite.mailBody', { title, url: step.url });
  const mail = `mailto:${encodeURIComponent(step.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const sheet = shell.shareSheet;
  return [
    ModalBody({},
      el(
        'div',
        { class: 'sh-stack' },
        el('p', { class: 'sh-help invite-for' }, t('invite.sendYourself', { email: step.email })),
        // The link and Copy link: "Copied" is the field's own; a copy that failed is said in the news below.
        component(CopyField, {
          class: 'invite-url',
          label: t('invite.link'),
          hideLabel: true,
          copyLabel: t('share.copy'),
          value: step.url,
          copy: shell.copy,
          onCopy: (_url: string, done: boolean) => {
            setNews('');
            setNews(done ? '' : t('share.copyFailed'));
          },
          inputRef: (input: HTMLInputElement) => {
            queueMicrotask(() => {
              input.focus();
              input.setSelectionRange(0, 0);
              input.scrollLeft = 0;
            });
          },
        }),
        el(
          'div',
          { class: 'sh-copyrow' },
          component(Button, { href: mail, class: 'mail-invite', iconStart: 'mail', children: t('invite.mail') }),
          sheet === null
            ? null
            : component(Button, {
                class: 'share-invite',
                onClick: () =>
                  void sheet({ title: subject, text: t('invite.sheetText', { title }), url: step.url }).then((done) => {
                    setNews('');
                    setNews(done ? t('invite.shared') : '');
                  }),
                children: t('invite.sheet'),
              }),
        ),
        el('p', { class: 'ac-error invite-news', role: 'status' }, news),
        el(
          'ul',
          { class: 'sh-warnings' },
          step.unsent ? el('li', { class: 'invite-unsent' }, t('invite.warnUnsent')) : null,
          el('li', {}, t('invite.warnSelf')),
          el('li', {}, t('invite.warnAddress', { email: step.email })),
          el('li', {}, t('invite.warnCopy')),
        ),
      ),
    ),
    ModalButtons(component(Button, { variant: 'primary', class: 'invite-back', onClick: back, children: t('share.done') })),
  ];
}

/** The people step: the invite row (a paid account's), its help and the people; the footer's lock note and Done. */
function People(shell: Shell, open: Accessor<string | null>, setOpen: (id: string | null) => void, link: (step: LinkStep) => void): Child {
  const { t, sharing, workspace, ui } = shell;
  const [email, setEmail] = createSignal('');
  const [role, setRole] = createSignal<InviteRole>('editor');
  const [busy, setBusy] = createSignal(false);
  const [news, setNews] = createSignal('');
  const say = (text: string): void => {
    setNews('');
    setNews(text);
  };
  const records = createMemo(() => openShares(shell));
  // Each person by their invite's ID, so a row (its settings open, its focus) outlives the record written again under it.
  const people = createMemo(() => records().flatMap((record) => record.invites.filter((invite) => invite.removedAt === null).map((invite) => invite.id)), [], { equals: (a, b) => a.length === b.length && a.every((id, at) => id === b[at]) });
  const submit = async (): Promise<void> => {
    if (busy()) return;
    const address = canonicalEmail(email());
    const book = workspace.book();
    if (book === null) return;
    if (address === null) {
      say(t('invite.error.email'));
      focusOn(shell, '#invite-email');
      return;
    }
    if (records().some((held) => held.invites.some((invite) => invite.removedAt === null && invite.email === address && standingOf(invite, sharing.listedOf(held, invite), shell.now()) !== 'ended'))) {
      say(t('invite.error.already', { email: address }));
      return;
    }
    setBusy(true);
    const invited = await sharing.invite({ bookId: book.id, email: address, role: role(), notes: ROLE_NOTES[role()], export: false, endsAt: null, chapters: null }).finally(() => setBusy(false));
    if (invited.kind === 'invited' || invited.kind === 'unsent') {
      setEmail('');
      link({ email: address, url: invited.url, unsent: invited.kind === 'unsent' });
      return;
    }
    say(problemOf(shell, invited));
  };
  return [
    ModalBody({},
      when(
        () => sharing.paid(),
        () => [
          el(
            'form',
            {
              class: 'sh-invite',
              noValidate: true,
              onSubmit: (event: SubmitEvent) => {
                event.preventDefault();
                void submit();
              },
            },
            el(
              'div',
              { class: 'sh-grow' },
              el('label', { class: 'visually-hidden', for: 'invite-email' }, t('invite.email')),
              component(TextField, {
                id: 'invite-email',
                type: 'email',
                autocomplete: 'off',
                placeholder: t('invite.emailPlaceholder'),
                autofocus: true,
                'aria-describedby': 'invite-role-help invite-news',
                get value() {
                  return email();
                },
                onChange: (value: string) => {
                  setEmail(value);
                },
              }),
            ),
            el(
              'div',
              {},
              el('label', { class: 'visually-hidden', for: 'invite-role' }, t('invite.role')),
              component(Select, {
                id: 'invite-role',
                // Its share of the row: at least its own width, the whole row on a phone.
                fullWidth: true,
                options: INVITE_ROLES.map((each) => ({ value: each, label: t(`invite.roleName.${each}`) })),
                get value() {
                  return role();
                },
                'aria-describedby': 'invite-role-help',
                onChange: (picked: string) => {
                  setRole(INVITE_ROLES.find((known) => known === picked) ?? 'editor');
                },
              }),
            ),
            component(Button, {
              variant: 'primary',
              type: 'submit',
              class: 'invite-send',
              get loading() {
                return busy();
              },
              get children() {
                return busy() ? t('invite.inviting') : t('invite.invite');
              },
            }),
          ),
          el('p', { class: 'sh-help', id: 'invite-role-help' }, () => t(`invite.roleHelp.${role()}`)),
        ],
        () => el('p', { class: 'invite-purchase' }, t('invite.purchase')),
      ),
      el('p', { id: 'invite-news', class: 'ac-error c-modal__error', role: 'status' }, news),
      // RC16: what stopped the copy, said until a reconcile finishes clean.
      CopyProblem(shell, () => workspace.book()?.id),
      el(
        'ul',
        { class: 'sh-people', 'aria-label': t('invite.people') },
        el(
          'li',
          { class: 'sh-person' },
          component(Avatar, { tone: 'owner', children: mark('user') }),
          el('div', { class: 'sh-grow' }, el('strong', {}, t('invite.you')), el('div', { class: 'sh-help' }, t('invite.youHelp'))),
          el('span', { class: 'sh-help' }, t('invite.owner')),
        ),
        each(people, (id) =>
          whenSome(
            () => holderOf(records(), id),
            (held) =>
              whenSome(
                () => held().invites.find((invite) => invite.id === id),
                (invite) => Person(shell, held, invite, open, setOpen, link, say),
              ),
          ),
        ),
      ),
      when(
        () => sharing.available(),
        () =>
          el(
            'p',
            { class: 'sh-help invite-links' },
            component(Button, {
              variant: 'link',
              class: 'make-link',
              iconStart: 'link',
              onClick: () => {
                ui.setDialog({ kind: 'share' });
                // The dialog stays open with new contents, so its autofocus doesn't run: the link form's first field (or,
                // unpaid, its heading) takes the focus.
                focusOn(shell, sharing.paid() ? '#share-name' : '#dialog-heading');
              },
              children: t('invite.makeLink'),
            }),
          ),
      ),
    ),
    // The foot's note at its left, Done at its right.
    ModalFoot(
      {},
      ModalNote(el('span', { class: 'sh-foot-lock' }, mark('lock'), t('invite.lockNote'))),
      ModalActions(component(Button, { class: 'share-done', onClick: () => ui.setDialog(null), children: t('share.done') })),
    ),
  ];
}

/** The Share-with-people dialog's contents: its head, then the people or the invite link's step. */
export function InviteDialog(shell: Shell, person: string | null): Child {
  const { t, sharing, workspace } = shell;
  // DI8: where each person stands, as the relay says now.
  onMount(() => {
    const book = workspace.book();
    if (book !== null) void sharing.refreshBook(book.id);
  });
  const [step, setStep] = createSignal<LinkStep | null>(null);
  const [open, setOpen] = createSignal<string | null>(person);
  const title = (): string => bookTitle(shell);
  return [
    Head(shell, () => {
      const link = step();
      return link === null ? t('invite.title', { title: title() }) : t('invite.linkTitle', { email: link.email });
    }),
    whenSome(
      step,
      (link) =>
        Link(shell, link(), () => {
          setStep(null);
          focusOn(shell, '#dialog-heading');
        }),
      () =>
        People(shell, open, setOpen, (link) => {
          setStep(link);
          focusOn(shell, '#dialog-heading');
        }),
    ),
  ];
}
